import React, { useState } from 'react';
import { X, Search, Filter, Mail, UserCheck, Clock, Zap, Split, Wand, UserRoundSearch } from 'lucide-react';

interface NodePaletteProps {
  onAddNode: (nodeType: string, position: { x: number; y: number }) => void;
  onClose: () => void;
}

interface NodeType {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: React.ReactNode;
  color: string;
}

const NODE_TYPES: NodeType[] = [
  {
    id: 'FetchLeadsActivity',
    name: 'Fetch Leads',
    description: 'Get leads from your dataset based on filters',
    category: 'Data',
    icon: <Search className="w-5 h-5" />,
    color: 'bg-green-500'
  },
  {
    id: 'LeadFilterActivity',
    name: 'Filter Leads',
    description: 'Apply custom filtering criteria to leads',
    category: 'Data',
    icon: <Filter className="w-5 h-5" />,
    color: 'bg-gray-500'
  },
  {
    id: 'EmailEnrichmentActivity',
    name: 'Email Enrichment',
    description: 'Find and verify email addresses for leads',
    category: 'Enrichment',
    icon: <UserRoundSearch className="w-5 h-5" />,
    color: 'bg-blue-500'
  },
  {
    id: 'LinkedInEnrichmentActivity',
    name: 'LinkedIn Enrichment',
    description: 'Enrich leads with LinkedIn profile data',
    category: 'Enrichment',
    icon: <UserCheck className="w-5 h-5" />,
    color: 'bg-purple-500'
  },
  {
    id: 'PersonalizedMessageActivity',
    name: 'Generate Message',
    description: 'Create personalized messages using AI',
    category: 'AI Generation',
    icon: <Wand className="w-5 h-5" />,
    color: 'bg-orange-500'
  },
  {
    id: 'EmailSendingActivity',
    name: 'Send Email',
    description: 'Send personalized emails to leads',
    category: 'Communication',
    icon: <Mail className="w-5 h-5" />,
    color: 'bg-purple-500'
  },
  {
    id: 'EmailReplyWaitActivity',
    name: 'Wait for Reply',
    description: 'Wait for email replies before continuing',
    category: 'Flow Control',
    icon: <Clock className="w-5 h-5" />,
    color: 'bg-yellow-500'
  },
  {
    id: 'EmailReplyClassificationActivity',
    name: 'Classify Reply',
    description: 'Classify email replies based on interest criteria',
    category: 'AI Analysis',
    icon: <Split className="w-5 h-5" />,
    color: 'bg-red-500'
  }
];

const CATEGORIES = Array.from(new Set(NODE_TYPES.map(nodeType => nodeType.category)));

export const NodePalette: React.FC<NodePaletteProps> = ({
  onAddNode,
  onClose
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredNodes = NODE_TYPES.filter(nodeType => {
    const matchesSearch = nodeType.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         nodeType.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || nodeType.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleNodeClick = (nodeType: string) => {
    // Add node at center of visible canvas area
    const position = {
      x: 300 + Math.random() * 200, // Add some randomness to avoid overlap
      y: 200 + Math.random() * 100
    };
    onAddNode(nodeType, position);
  };

  return (
    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 max-h-[50vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Zap className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Add Node</h3>
          </div>
          <button
            id="node-palette-close-button"
            aria-label="Close"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search and Filter */}
        <div className="p-6 border-b border-gray-200 space-y-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search nodes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('All')}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                selectedCategory === 'All'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {CATEGORIES.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Activities Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredNodes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No nodes found matching your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredNodes.map(nodeType => (
                <div
                  id={`node-palette-node-${nodeType.id}`}
                  role='button'
                  tabIndex={0}
                  key={nodeType.id}
                  onClick={() => handleNodeClick(nodeType.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleNodeClick(nodeType.id);
                    }
                  }}
                  className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all group"
                >
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg text-white ${nodeType.color} group-hover:scale-110 transition-transform`}>
                      {nodeType.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 mb-1">
                        {nodeType.name}
                      </h4>
                      <p className="text-sm text-gray-600 mb-2">
                        {nodeType.description}
                      </p>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                        {nodeType.category}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <p className="text-sm text-gray-600">
            💡 Click any node to add it to your workflow canvas
          </p>
        </div>
      </div>
    </div>
  );
};